
import React, { useContext, useState, useEffect, useMemo } from "react";
import inviteIcon from "../../images/inviteicon.png";
import profileIcon from "../../images/profileicon.png";
import styles from "./styles/People.module.css";
import * as NotificationAPI from '../../services/NotificationServices';
import { removeUserFromClassroom as removeUserFromClassroomAPI, changeUserRole as changeUserRoleAPI } from '../../services/ClassroomServices';
import AuthContext from '../../context/AuthContext';
import { ClassroomContext } from "../../context/ClassroomContext";
import ClassroomInviteModal from "./ClassroomInviteModal";

const People = () => {
    const { userId } = useContext(AuthContext);
    const { classroom, setClassroom, isTeacher, isOwner } = useContext(ClassroomContext); 
    
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);

    useEffect(() => {
        if (classroom) {
            setStudents(classroom.students || []);
            setTeachers(classroom.teachers || []);
        } else {
            setStudents([]);
            setTeachers([]);
        }
    }, [classroom]);

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [role, setRole] = useState(null);
    const [actionError, setActionError] = useState('');

    const inviteUserToClassroom = async (invitedUserId, role) => {
        try {
            await NotificationAPI.sendInvite(invitedUserId, classroom._id, classroom.name, role);
        } catch (error) {
            console.error("Error inviting user:", error);
        }
    };

    const removeUserFromClassroom = async (userIdToRemove, roleToRemove) => {
        if (!classroom || !classroom._id) {
            console.error("Classroom not loaded.");
            return;
        }

        try {
            setActionError('');
            await removeUserFromClassroomAPI(classroom._id, userIdToRemove, roleToRemove);

            setClassroom(prevClassroom => {
                if (!prevClassroom) return null;

                let updatedStudents = [...(prevClassroom.students || [])];
                let updatedTeachers = [...(prevClassroom.teachers || [])];

                if (roleToRemove === 'student') {
                    updatedStudents = updatedStudents.filter((student) => student._id !== userIdToRemove);
                } else if (roleToRemove === 'teacher') {
                    updatedTeachers = updatedTeachers.filter((teacher) => teacher._id !== userIdToRemove);
                }
                
                return {
                    ...prevClassroom,
                    students: updatedStudents,
                    teachers: updatedTeachers,
                };
            });

        } catch (error) {
            console.error("Error removing user from classroom:", error);
            // The server can refuse (not authorized, owner's teacher entry), and the roster
            // just silently not changing looks like a dead button.
            setActionError(error.response?.data?.message || 'Could not remove that person. Please try again.');
        }
    };

    const changeUserRole = async (userIdToChange, currentRole) => {
        if (!classroom || !classroom._id) {
            console.error("Classroom not loaded.");
            return;
        }
        const newRole = currentRole === 'student' ? 'teacher' : 'student';
        try {
            setActionError('');
            await changeUserRoleAPI(classroom._id, userIdToChange, newRole);

            setClassroom(prevClassroom => {
                if (!prevClassroom) return null;

                let updatedStudents = [...(prevClassroom.students || [])];
                let updatedTeachers = [...(prevClassroom.teachers || [])];
                let userToMove;

                if (currentRole === 'student') {
                    userToMove = updatedStudents.find(student => student._id === userIdToChange);
                    if (userToMove) {
                        updatedStudents = updatedStudents.filter((student) => student._id !== userIdToChange);
                        
                        
                        
                        updatedTeachers = [...updatedTeachers, { ...userToMove }];
                    }
                } else if (currentRole === 'teacher') {
                    userToMove = updatedTeachers.find(teacher => teacher._id === userIdToChange);
                    if (userToMove) {
                        updatedTeachers = updatedTeachers.filter((teacher) => teacher._id !== userIdToChange);
                        updatedStudents = [...updatedStudents, { ...userToMove }];
                    }
                }

                return {
                    ...prevClassroom,
                    students: updatedStudents,
                    teachers: updatedTeachers,
                };
            });

        } catch (error) {
            console.error("Error changing user role:", error);
            setActionError(error.response?.data?.message || 'Could not change that role. Please try again.');
        }
    };

    // Legacy data (and, until it was blocked server-side, joining your own class by code)
    // could leave someone on the teachers and students rosters at once. Surfacing it here
    // lets them take themselves off the students list without leaving the classroom.
    const isSelfDuplicated = useMemo(
        () => (isTeacher || isOwner) && students.some((student) => student._id === userId),
        [isTeacher, isOwner, students, userId]
    );

    const removeSelfFromStudents = () => {
        if (!window.confirm("Remove yourself from this classroom's students list? You'll stay on as a teacher.")) return;
        removeUserFromClassroom(userId, 'student');
    };

    const existingUserIds = useMemo(() => {
        if (!classroom) return [];
        return [...(classroom.students || []), ...(classroom.teachers || [])].map(user => user._id);
    }, [classroom]);


    return (
        <div className={styles.peopleContainer}>
            {isInviteModalOpen && (
                <ClassroomInviteModal
                    isOpen={isInviteModalOpen}
                    onClose={() => setIsInviteModalOpen(false)}
                    role={role}
                    existingUsers={existingUserIds} 
                    inviteUserToClassroom={inviteUserToClassroom}
                    classroomId={classroom?._id}
                />
            )}
            {actionError && <div className={styles.actionError} role="alert">{actionError}</div>}
            <div className={styles.categoryContainer}>
                <div className={styles.header}>
                    <h2>Teachers</h2>
                    {isOwner && ( 
                        <div className={styles.inviteButton}
                             onClick={() => { setRole('teacher'); setIsInviteModalOpen(true); }}
                             title="Invite Teacher"
                        >
                            <img src={inviteIcon} alt="Invite Teacher" className={styles.inviteIcon} />
                        </div>
                    )}
                </div>
                <div className={styles.list}>
                    {teachers.map((teacher) => (
                        <div key={teacher._id} className={styles.item}>
                            <div className={styles.profileInfo}>
                                <img 
                                    src={teacher.profilePicture || profileIcon} 
                                    alt={teacher.username || 'Teacher'} 
                                    className={styles.profileIcon} 
                                />
                                {teacher.username || 'Unnamed Teacher'}
                                {teacher._id === userId && <span className={styles.youTag}>You</span>}
                            </div>
                            {isOwner && teacher._id !== userId && (
                                <div className={styles.actions}>
                                    <button onClick={() => changeUserRole(teacher._id, 'teacher')} className={styles.changeRoleButton}>
                                        Demote
                                    </button>
                                    <button onClick={() => removeUserFromClassroom(teacher._id, 'teacher')} className={styles.removeButton}>
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {teachers.length === 0 && <p className={styles.emptyListMessage}>No teachers in this classroom.</p>}
                </div>
            </div>

            <div className={styles.categoryContainer}>
                <div className={styles.header}>
                    <h2>Students</h2>
                    {isTeacher && (
                        <div className={styles.inviteButton}
                             onClick={() => { setRole('student'); setIsInviteModalOpen(true); }}
                             title="Invite Student" 
                        >
                            <img src={inviteIcon} alt="Invite Student" className={styles.inviteIcon} />
                        </div>
                    )}
                </div>
                {isSelfDuplicated && (
                    <div className={styles.selfDuplicateNotice}>
                        You're listed as both a teacher and a student here. Removing your student
                        entry won't affect your teacher access.
                    </div>
                )}
                <div className={styles.list}>
                    {students.map((student) => (
                        <div key={student._id} className={styles.item}>
                            <div className={styles.profileInfo}>
                                <img
                                    src={student.profilePicture || profileIcon}
                                    alt={student.username || 'Student'}
                                    className={styles.profileIcon}
                                />
                                {student.username || 'Unnamed Student'}
                                {student._id === userId && <span className={styles.youTag}>You</span>}
                            </div>
                            {student._id === userId ? (
                                isSelfDuplicated && (
                                    <div className={styles.actions}>
                                        <button
                                            onClick={removeSelfFromStudents}
                                            className={styles.removeButton}
                                            title="Take yourself off the students list and stay on as a teacher"
                                        >
                                            Remove me from students
                                        </button>
                                    </div>
                                )
                            ) : isTeacher && (
                                <div className={styles.actions}>
                                    <button onClick={() => changeUserRole(student._id, 'student')} className={styles.changeRoleButton}>
                                        Promote
                                    </button>
                                    <button onClick={() => removeUserFromClassroom(student._id, 'student')} className={styles.removeButton}>
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {students.length === 0 && <p className={styles.emptyListMessage}>No students in this classroom.</p>}
                </div>
            </div>
        </div>
    );
};

export default People;